import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as React from 'react';
import { Conflict, SimpleTooltip } from '@components/atoms';
import { Label } from '@components/atoms/typography';
import { cn } from '@utils';

const SWITCH_CLASS_NAME =
	'focus-visible:ring-ring focus-visible:ring-offset-background peer inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-success data-[state=unchecked]:bg-slate-300';

const SWITCH_THUMB_CLASS_NAME =
	'block h-4 w-4 rounded-full bg-white ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0';

export interface IToggleSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange' | 'title'> {
	title: string;
	description?: string;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	disabled?: boolean;
	disabledNote?: string;
}

export const ToggleSection = ({
	title,
	description,
	checked,
	onCheckedChange,
	disabled = false,
	disabledNote,
	children,
	className,
	id,
	...props
}: IToggleSectionProps) => {
	const isOpen = checked && !disabled;
	// React 17 has no useId. Keep the fallback for callers that omit id so a re-render does not move the label.
	const generatedId = React.useRef(`toggle-section-${Math.random().toString(36).substring(2)}`);
	const switchId = id || generatedId.current;
	const disabledNoteId = disabled && disabledNote ? `${switchId}-disabled-note` : undefined;

	return (
		<section
			data-testid="toggle-section"
			className={cn(
				'flex flex-col gap-2 rounded-lg border border-borderSeparator p-4',
				isOpen ? 'bg-bgPrimary' : 'bg-white',
				className
			)}
			{...props}
		>
			<div data-testid="toggle-section-header" className="flex items-center gap-3">
				<SwitchPrimitives.Root
					id={switchId}
					data-testid="toggle-section-switch"
					className={SWITCH_CLASS_NAME}
					checked={checked}
					disabled={disabled}
					onCheckedChange={onCheckedChange}
					aria-describedby={disabledNoteId}
				>
					<SwitchPrimitives.Thumb className={SWITCH_THUMB_CLASS_NAME} />
				</SwitchPrimitives.Root>
				<div data-testid="toggle-section-copy" className="flex min-w-0 flex-1 flex-col gap-1">
					<Label
						htmlFor={switchId}
						label={title}
						className={disabled ? '[&_span]:text-textDisabled' : undefined}
					/>
					{description ? (
						<p className={cn('text-sm', disabled ? 'text-textDisabled' : 'text-textDarkGray')}>{description}</p>
					) : null}
				</div>
				{disabled && disabledNote ? (
					<>
						<span id={disabledNoteId} className="sr-only">
							{disabledNote}
						</span>
						<SimpleTooltip content={disabledNote} ariaLabel={disabledNote}>
							<Conflict
								aria-hidden
								className="h-6 w-6 shrink-0 text-error"
								data-testid="toggle-section-note"
							/>
						</SimpleTooltip>
					</>
				) : null}
			</div>
			{isOpen && children ? <div data-testid="toggle-section-content">{children}</div> : null}
		</section>
	);
};
